import { useEffect, useState, useRef } from 'react';
import {
    Box, Typography, Button, Select, MenuItem, FormControl, InputLabel,
    IconButton, CircularProgress, Divider, Collapse, TextField, LinearProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import { getSalesDocuments, uploadSalesDocument, deleteSalesDocument, generateSaleForms, generateSignLink, agentSign } from '../../api/salesApi';
import SignaturePad from '../common/signaturePad';

const PP = "'Poppins', sans-serif";

// Why an upload failed, in the person's words where we have them and the server's where
// we do not. The old version knew three response shapes — {message}, {errors}, {title} —
// and fell back to a bare "Upload failed." for anything else, which is exactly what prod
// has been returning: a short body in none of those shapes. Now an unrecognised body is
// SHOWN rather than swallowed, with the status code, so the next failure diagnoses
// itself instead of needing a log dig. (user 2026-10-13)
const uploadFailureReason = (err) => {
    const res = err?.response;
    const data = res?.data;

    if (!res) {
        // No response at all: the request never completed — offline, DNS, a dropped
        // connection, or the browser cancelling it.
        return err?.message
            ? `Could not reach the server (${err.message}). Check your connection and try again.`
            : 'Could not reach the server. Check your connection and try again.';
    }

    const fieldErr = data?.errors && Object.values(data.errors).flat?.()[0];
    if ((fieldErr || '').toLowerCase().includes('request body too large')) {
        return 'File is too large to upload (max 100 MB).';
    }

    const known = data?.message || fieldErr || data?.title;
    if (known) return known;

    // A plain-text body IS the server's own sentence (BadRequest("No file provided.")
    // serialises this way) — show it as written, with no wrapper around it.
    if (typeof data === 'string' && data.trim() && !/[<>]/.test(data) && data.length < 200) {
        return data.trim();
    }

    // Unrecognised body. Show it — trimmed, because it could be an HTML error page —
    // together with the status, so the reason reaches the person on screen.
    const raw = typeof data === 'string'
        ? data.trim()
        : (data ? JSON.stringify(data) : '');
    const snippet = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
    return snippet
        ? `Upload failed (${res.status}): ${snippet}`
        : `Upload failed (${res.status}) — the server gave no reason.`;
};
const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api$/, '');

const CATEGORIES = [
    { value: 'Quotation', label: 'Signed Quotation' },
    { value: 'Signed',  label: 'Signed Document' },
    { value: 'Deposit', label: 'Deposit' },
    { value: 'Payment', label: 'Payment' },
    { value: 'Other',   label: 'Other' },
];

// 'Application' docs are handled entirely by the checklist below (their own upload +
// display), so they are deliberately NOT in the generic dropdown or the section list.
const SECTION_ORDER = ['Quotation', 'Signed', 'Deposit', 'Payment', 'Other'];
const SECTION_LABELS = {
    Quotation: 'Signed Quotation',
    Signed:  'Signed Documents',
    Deposit: 'Deposit Document',
    Payment: 'Payment Documents',
    Other:   'Other Documents',
};

// Application document checklists. Each item is a required upload; the panel shows
// uploaded / missing status + a per-item upload button. Uploaded under category
// "Application" with the item's `key` as the title so the checklist matches them back.
// Residential and Commercial have DIFFERENT required docs. (user 2026-07-01)
const RESIDENTIAL_CHECKLIST = [
    { key: 'Applicant IC (Front & Back)',            note: 'For TNB use only' },
    { key: 'Latest TNB Bills x 3 Months',            note: 'Combine into one PDF' },
    { key: 'Cukai Taksiran / Geran / SPA / Tenancy', note: 'Any one that applies' },
    { key: 'TNB Meter Photo (far & near)',           note: 'Two shots: far and near' },
    { key: 'TIN Number Screenshot (LHDN)',           note: 'From the LHDN website' },
];

const COMMERCIAL_CHECKLIST = [
    { key: 'Applicant IC (Front & Back)',   note: 'For TNB use only' },
    { key: 'Ownership / Tenancy Proof',     note: 'Any one: Cukai Taksiran/Pintu/Tanah, Land Title (地契), Tenancy Agreement, or SPA' },
    { key: 'LHDN Form C',                   note: 'Company tax — past 3 years' },
    { key: 'SSM Documents',                 note: 'SSM Super Form + Forms 9, 24, 49, 55' },
    { key: 'TNB Bill (12 months)',          note: 'Latest 12 months' },
    { key: 'TNB Meter Photo',               note: 'Far & near' },
];

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatBytes(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRM(n) {
    const v = Number(n) || 0;
    return `RM ${v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Customer signature link lives on the dedicated sign subdomain (sign.astern-tech.com)
// in production; local/dev keeps the current origin so testing still works. DNS/nginx
// for sign.astern-tech.com are live (user 2026-07-05).
const SIGN_ORIGIN = (() => {
    const o = (typeof window !== 'undefined' && window.location.origin) || '';
    return /sales\.nexwaveintl\.com/i.test(o) ? 'https://sign.astern-tech.com' : o;
})();

export default function SalesDocumentsPanel({ salesId, canEdit, signedOn, depositOn, isCommercial = false, customerSigned = false, agentSigned = false, canAgentSign = true, extraActions = null, afterActions = null, onDocsChanged, paidTotal = 0, totalPrice = 0, refreshKey = 0 }) {
    const [docs, setDocs] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [docsListOpen, setDocsListOpen] = useState(true);

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadCategory, setUploadCategory] = useState('Signed');
    const [uploadAmount, setUploadAmount] = useState('');
    const [uploadDate, setUploadDate] = useState('');   // payment date (Payment docs)
    const [uploadTitle, setUploadTitle] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);

    // Application-checklist (TNB/LHDN) per-item upload — one-click: pick file → upload.
    const checklistFileRef = useRef(null);
    const pendingChecklistItem = useRef(null);
    const [checklistUploadingKey, setChecklistUploadingKey] = useState(null);
    const [checklistError, setChecklistError] = useState('');

    const [generatingForms, setGeneratingForms] = useState(false);
    const [generateError, setGenerateError] = useState('');

    const [signingLink, setSigningLink] = useState('');
    const [signingLinkError, setSigningLinkError] = useState('');
    const [generatingLink, setGeneratingLink] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const linkInputRef = useRef(null);

    // Agent (sales-person) signature — residential dual-party signing.
    const agentSigApiRef = useRef(null);
    const [agentSigning, setAgentSigning] = useState(false);
    const [agentSignError, setAgentSignError] = useState('');
    // Local mirror so the status flips immediately after the agent signs, without
    // waiting for the parent's sale refetch.
    const [agentSignedLocal, setAgentSignedLocal] = useState(agentSigned);
    useEffect(() => { setAgentSignedLocal(agentSigned); }, [agentSigned]);

    const fetchDocs = () => {
        if (!salesId) return;
        setLoadingDocs(true);
        getSalesDocuments(salesId)
            .then(r => setDocs(r.data || []))
            .catch(() => setDocs([]))
            .finally(() => setLoadingDocs(false));
    };

    // Re-fetch on salesId AND refreshKey — the parent bumps refreshKey after an edit
    // save (which regenerates the forms with new filenames), so the links stay current
    // instead of pointing at the now-deleted previous draft (404).
    useEffect(() => { fetchDocs(); }, [salesId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDelete = async (doc) => {
        if (!window.confirm(`Remove "${doc.fileName || doc.title}"?`)) return;
        try {
            await deleteSalesDocument(doc.salesDocumentId);
            fetchDocs();
            onDocsChanged?.();
        } catch {
            alert('Failed to delete document.');
        }
    };

    const isPaymentCat = uploadCategory === 'Deposit' || uploadCategory === 'Payment';

    const handleUpload = async () => {
        if (!selectedFile) { setUploadError('Please select a file.'); return; }
        // Deposit can only have ONE document — delete the existing one to replace it.
        if (uploadCategory === 'Deposit' && docs.some(d => d.category === 'Deposit')) {
            setUploadError('A deposit document already exists. Delete it first to upload another.');
            return;
        }
        if (isPaymentCat) {
            const amt = Number(uploadAmount);
            if (!uploadAmount || isNaN(amt) || amt <= 0) {
                setUploadError('Please enter the payment amount (RM).');
                return;
            }
            // The date the customer PAID, now required. It was optional, and skipping it
            // was the path of least resistance, so the timeline fell back to the upload
            // date on roughly half of all slips — which reads as a payment date and is
            // not one. A Deposit on a sale that already HAS a deposit date is the one
            // exception: there is no field to fill, because the sale's own date is used.
            // (user 2026-10-12)
            const dateNeeded = uploadCategory === 'Payment' || !depositOn;
            if (dateNeeded && !uploadDate) {
                setUploadError(uploadCategory === 'Deposit'
                    ? 'Please enter the deposit date.'
                    : 'Please enter the date the customer paid.');
                return;
            }
        }
        setUploadError('');
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('salesId', salesId);
            fd.append('category', uploadCategory);
            fd.append('title', uploadTitle.trim() || selectedFile.name);
            fd.append('file', selectedFile);
            if (isPaymentCat) {
                fd.append('amount', String(Number(uploadAmount)));
                // Auto-label: Deposit category = "Deposit"; Payment category auto-numbers
                // "Payment N" from the highest existing Payment number + 1.
                let label = 'Deposit';
                if (uploadCategory === 'Payment') {
                    const nums = docs
                        .filter(d => d.category === 'Payment')
                        .map(d => { const m = /Payment\s*(\d+)/i.exec(d.paymentLabel || ''); return m ? Number(m[1]) : 0; });
                    const next = (nums.length ? Math.max(...nums) : 0) + 1;
                    label = `Payment ${next}`;
                }
                fd.append('paymentLabel', label);
            }
            // Payment/Deposit slip date. For a Deposit doc this also sets the sale's
            // deposit date (server-side, only when the sale has none yet).
            if (isPaymentCat && uploadDate) {
                fd.append('docDate', new Date(`${uploadDate}T12:00:00`).toISOString());
            } else if (uploadCategory === 'Deposit' && depositOn) {
                // No field was shown, because the sale already carries a deposit date —
                // so use it rather than leaving the slip undated. This is where most of
                // the undated deposits came from: the form had nowhere to type it.
                fd.append('docDate', new Date(depositOn).toISOString());
            }
            await uploadSalesDocument(fd);
            setSelectedFile(null);
            setUploadAmount('');
            setUploadDate('');
            setUploadTitle('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            fetchDocs();
            onDocsChanged?.();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[upload] failed', {
                status: err?.response?.status,
                data: err?.response?.data,
                category: uploadCategory,
                file: selectedFile && { name: selectedFile.name, size: selectedFile.size, type: selectedFile.type },
            });
            setUploadError(uploadFailureReason(err));
        } finally {
            setUploading(false);
        }
    };

    // Application checklist: open the file picker for a specific item.
    const handleChecklistPick = (itemKey) => {
        setChecklistError('');
        pendingChecklistItem.current = itemKey;
        checklistFileRef.current?.click();
    };

    // Application checklist: upload the chosen file(s) straight away (category "Application",
    // title = the checklist item), so they land in the checklist AND the documents list.
    // Multiple files per item are allowed — each upload APPENDS (no replace/delete), so a
    // customer can attach e.g. all three TNB bill months or both meter-photo shots.
    const handleChecklistFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        const itemKey = pendingChecklistItem.current;
        if (checklistFileRef.current) checklistFileRef.current.value = '';
        if (!files.length || !itemKey) return;
        setChecklistUploadingKey(itemKey);
        setChecklistError('');
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append('salesId', salesId);
                fd.append('category', 'Application');
                fd.append('title', itemKey);
                fd.append('file', file);
                await uploadSalesDocument(fd);
            }
            fetchDocs();
            onDocsChanged?.();
        } catch (err) {
            // The checklist uploads several files in a loop, so the burst of identical
            // failures in the prod log almost certainly came through here. Name the file
            // that failed — "Upload failed" on a five-file batch says nothing about which.
            // eslint-disable-next-line no-console
            console.error('[upload:checklist] failed', {
                status: err?.response?.status, data: err?.response?.data, item: itemKey,
            });
            setChecklistError(uploadFailureReason(err));
        } finally {
            setChecklistUploadingKey(null);
            pendingChecklistItem.current = null;
        }
    };

    const handleGenerateForms = async () => {
        setGenerateError('');
        setGeneratingForms(true);
        try {
            await generateSaleForms(salesId, false);
            fetchDocs();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || 'Generate forms failed.';
            setGenerateError(typeof msg === 'string' ? msg : 'Generate forms failed.');
        } finally {
            setGeneratingForms(false);
        }
    };

    const handleGetSignLink = async () => {
        setSigningLinkError('');
        setSigningLink('');
        setLinkCopied(false);
        setGeneratingLink(true);
        try {
            // Re-signing ("Sign again"): regenerate a fresh, unsigned form set first
            // so the new link presents up-to-date documents with the previous
            // customer signature cleared, ready to be signed again.
            if (signedOn) {
                await generateSaleForms(salesId, false);
                fetchDocs();
                onDocsChanged?.();
            }
            // Button = explicit regenerate (mint a fresh token, expiring the old link).
            const res = await generateSignLink(salesId, true);
            const fullUrl = `${SIGN_ORIGIN}/sign/${res.data.token}`;
            setSigningLink(fullUrl);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || 'Failed to generate signing link.';
            setSigningLinkError(typeof msg === 'string' ? msg : 'Failed to generate signing link.');
        } finally {
            setGeneratingLink(false);
        }
    };

    // Auto-load the signing link on open (no click needed) for sales the customer
    // hasn't signed yet — idempotent get-or-create, so it shows the existing link or
    // mints one once. Signed sales skip this (token consumed) and use the
    // "Re-sign (new link)" button instead. Silent on failure (e.g. forms not generated).
    useEffect(() => {
        if (signedOn) return;
        let cancelled = false;
        generateSignLink(salesId, false)
            .then(res => { if (!cancelled && res?.data?.token) setSigningLink(`${SIGN_ORIGIN}/sign/${res.data.token}`); })
            .catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesId, signedOn]);

    const handleCopyLink = () => {
        if (!signingLink) return;
        navigator.clipboard.writeText(signingLink).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2500);
        }).catch(() => {
            // Fallback: select text
            if (linkInputRef.current) {
                linkInputRef.current.select();
                document.execCommand('copy');
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2500);
            }
        });
    };

    const handleAgentSign = async () => {
        setAgentSignError('');
        const png = agentSigApiRef.current?.getDataUrl?.() || '';
        if (agentSigApiRef.current?.isEmpty?.() || !png || png === 'data:image/png;base64,') {
            setAgentSignError('The signature pad is empty. Please draw your signature.');
            return;
        }
        setAgentSigning(true);
        try {
            await agentSign(salesId, { salesPersonSignaturePng: png });
            setAgentSignedLocal(true);
            // Refresh docs (the signed PDFs may now be released) and the parent sale.
            fetchDocs();
            onDocsChanged?.();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || 'Failed to submit signature.';
            setAgentSignError(typeof msg === 'string' ? msg : 'Failed to submit signature.');
        } finally {
            setAgentSigning(false);
        }
    };

    // Force a browser download of a single document (rather than opening it inline).
    const handleDownloadDoc = async (doc) => {
        if (!doc.fileUrl) return;
        const url = `${API_BASE}${doc.fileUrl}`;
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            const objUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objUrl;
            a.download = doc.fileName || doc.title || 'document';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objUrl);
        } catch {
            // Fallback: open in a new tab (the browser's own download/view).
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    // Download every file in a section as SEPARATE files (no zip). A small delay between
    // each lets the browser process them all (it may show a "download multiple files" prompt).
    const [downloadingAll, setDownloadingAll] = useState('');
    const handleDownloadAll = async (arr, cat) => {
        setDownloadingAll(cat);
        try {
            for (const d of (arr || [])) {
                if (!d.fileUrl) continue;
                await handleDownloadDoc(d);
                await new Promise(r => setTimeout(r, 400));
            }
        } finally { setDownloadingAll(''); }
    };

    // Group docs by category; only show Other section if Other docs exist
    const grouped = {};
    docs.forEach(d => {
        const cat = d.category || 'Other';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(d);
    });

    const visibleSections = SECTION_ORDER.filter(cat =>
        cat !== 'Other' ? true : (grouped['Other'] && grouped['Other'].length > 0)
    );

    const pct = totalPrice > 0 ? Math.min(100, (paidTotal / totalPrice) * 100) : 0;
    const fullyPaid = totalPrice > 0 && paidTotal >= totalPrice;
    const barColor = fullyPaid ? '#22C55E' : '#1A8CFF';
    const progressBar = (
        <Box sx={{ mb: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#334155', fontWeight: 600 }}>
                    {formatRM(paidTotal)} / {formatRM(totalPrice)}
                </Typography>
                {fullyPaid ? (
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        background: '#F0FDF4', border: '1px solid #BBF7D0',
                        borderRadius: '6px', px: 0.75, py: 0.15
                    }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#22C55E' }} />
                        <Typography sx={{ fontFamily: PP, fontSize: '0.68rem', color: '#15803D', fontWeight: 700 }}>
                            Fully Paid
                        </Typography>
                    </Box>
                ) : (
                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#64748B', fontWeight: 600 }}>
                        {pct.toFixed(0)}%
                    </Typography>
                )}
            </Box>
            <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                    height: 8, borderRadius: 4, background: '#E2E8F0',
                    '& .MuiLinearProgress-bar': { background: barColor, borderRadius: 4 }
                }}
            />
        </Box>
    );

    // A document only counts as "signed" (green marker) once every required party
    // has signed — customer for commercial, customer + agent for residential.
    const custSignedDone = !!(customerSigned || signedOn);
    const salesFullySigned = isCommercial ? custSignedDone : (custSignedDone && !!agentSignedLocal);

    const docsList = loadingDocs ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
        </Box>
    ) : (
        visibleSections.map((cat, idx) => (
            <Box key={cat} sx={{ mb: 2 }}>
                {idx > 0 && <Divider sx={{ mb: 1.5 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                    <Typography sx={{
                        fontFamily: PP, fontWeight: 600, fontSize: '0.75rem',
                        color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em'
                    }}>
                        {SECTION_LABELS[cat]}
                    </Typography>
                    {(grouped[cat]?.filter(d => d.fileUrl).length > 1) && (
                        <Button size="small" disabled={downloadingAll === cat}
                            startIcon={<DownloadIcon sx={{ fontSize: '0.9rem' }} />}
                            onClick={() => handleDownloadAll(grouped[cat], cat)}
                            sx={{ fontFamily: PP, fontSize: '0.68rem', fontWeight: 600, textTransform: 'none', color: '#1A8CFF', py: 0, minWidth: 0 }}>
                            {downloadingAll === cat ? 'Downloading…' : `Download all (${grouped[cat].filter(d => d.fileUrl).length})`}
                        </Button>
                    )}
                </Box>

                {cat === 'Payment' && progressBar}

                {(!grouped[cat] || grouped[cat].length === 0) ? (
                    <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#94A3B8', fontStyle: 'italic', mb: 0.5 }}>
                        No documents
                    </Typography>
                ) : (
                    grouped[cat].map(doc => {
                        const absoluteUrl = doc.fileUrl
                            ? `${API_BASE}${doc.fileUrl}`
                            : null;
                        return (
                            <Box key={doc.salesDocumentId} sx={{
                                display: 'flex', alignItems: 'center', gap: 0.75,
                                py: 0.55, px: 0.5, borderRadius: '6px',
                                '&:hover': { background: '#F8FAFC' }
                            }}>
                                {/* Left marker — green once the sale is fully signed, else
                                    neutral grey. Top-aligned to the title line. */}
                                <Box sx={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center', alignSelf: 'flex-start', mt: '4px' }}>
                                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: salesFullySigned ? '#22C55E' : '#CBD5E1' }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {absoluteUrl ? (
                                        <Typography
                                            component="a"
                                            href={absoluteUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                fontFamily: PP, fontSize: '0.8rem', fontWeight: 600, color: '#1A8CFF',
                                                textDecoration: 'none', display: 'block',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            {doc.title || doc.fileName}
                                        </Typography>
                                    ) : (
                                        <Typography sx={{
                                            fontFamily: PP, fontSize: '0.8rem', fontWeight: 600, color: '#0F172A',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                        }}>
                                            {doc.title || doc.fileName}
                                        </Typography>
                                    )}
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8' }}>
                                        {formatDate(doc.uploadedOn)}{doc.fileSize ? ` · ${formatBytes(doc.fileSize)}` : ''}
                                    </Typography>
                                    {(doc.amount != null || doc.paymentLabel) && (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#0F766E', fontWeight: 600 }}>
                                            {doc.paymentLabel ? doc.paymentLabel : 'Payment'}
                                            {doc.amount != null ? ` · ${formatRM(doc.amount)}` : ''}
                                            {doc.docDate ? ` · ${formatDate(doc.docDate)}` : ''}
                                        </Typography>
                                    )}
                                </Box>
                                {absoluteUrl && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDownloadDoc(doc)}
                                        sx={{ color: '#CBD5E1', '&:hover': { color: '#1A8CFF' }, flexShrink: 0 }}
                                        title="Download document"
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                )}
                                {canEdit && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDelete(doc)}
                                        sx={{ color: '#CBD5E1', '&:hover': { color: '#EF4444' }, flexShrink: 0 }}
                                        title="Remove document"
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        );
                    })
                )}
            </Box>
        ))
    );

    return (
        <Box sx={{ fontFamily: PP }}>
            {canEdit && (
                <>
                    {/* ── Actions (always visible): Signature · ClickUp · Regenerate ── */}
                    <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{
                            fontFamily: PP, fontWeight: 600, fontSize: '0.75rem',
                            color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75
                        }}>
                            {isCommercial ? 'Customer Signature' : 'Signatures'}
                        </Typography>

                        {/* Dual-party signature status. Residential needs BOTH the
                            customer and the agent; commercial needs the customer only. */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                            <SigChip label="Customer" done={!!(customerSigned || signedOn)} />
                            {!isCommercial && <SigChip label="Agent" done={!!agentSignedLocal} />}
                        </Box>

                        {/* Combined status: green only when ALL required parties have signed —
                            customer only for commercial, customer + agent for residential. */}
                        {(() => {
                            const custDone = !!(customerSigned || signedOn);
                            const allSigned = isCommercial ? custDone : (custDone && !!agentSignedLocal);
                            if (!allSigned) return null;
                            return (
                                <Box sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                                    background: '#F0FDF4', border: '1px solid #86EFAC',
                                    borderRadius: '8px', px: 1.25, py: 0.5, mb: 0.75
                                }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', flexShrink: 0 }} />
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#15803D', fontWeight: 700 }}>
                                        Fully signed
                                    </Typography>
                                </Box>
                            );
                        })()}

                        {signedOn ? (
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                                background: '#F0FDF4', border: '1px solid #BBF7D0',
                                borderRadius: '8px', px: 1.25, py: 0.5, mb: 0.75
                            }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#15803D', fontWeight: 500 }}>
                                    Signed on {new Date(signedOn).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </Typography>
                            </Box>
                        ) : null}

                        <Box>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={generatingLink}
                                onClick={handleGetSignLink}
                                sx={{
                                    fontFamily: PP, textTransform: 'none', fontSize: '0.78rem',
                                    borderColor: '#CBD5E1', color: '#475569', borderRadius: '8px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {generatingLink
                                    ? <><CircularProgress size={13} sx={{ mr: 0.75, color: '#94A3B8' }} />Generating...</>
                                    : (signedOn ? 'Re-sign (new link)' : 'Regenerate link')}
                            </Button>
                        </Box>

                        {signingLinkError && (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#EF4444', mt: 0.5 }}>
                                {signingLinkError}
                            </Typography>
                        )}

                        {signingLink && (
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                    <input
                                        ref={linkInputRef}
                                        readOnly
                                        value={signingLink}
                                        style={{
                                            flex: 1, fontFamily: PP, fontSize: '0.72rem',
                                            border: '1px solid #E2E8F0', borderRadius: '6px',
                                            padding: '4px 8px', color: '#334155',
                                            background: '#F8FAFC', minWidth: 0
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleCopyLink}
                                        sx={{
                                            fontFamily: PP, textTransform: 'none', fontSize: '0.72rem',
                                            borderColor: linkCopied ? '#22C55E' : '#CBD5E1',
                                            color: linkCopied ? '#15803D' : '#475569',
                                            borderRadius: '6px', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 56
                                        }}
                                    >
                                        {linkCopied ? 'Copied' : 'Copy'}
                                    </Button>
                                </Box>
                                <Button
                                    component="a"
                                    href={signingLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                        mt: 0.25, fontFamily: PP, textTransform: 'none', fontSize: '0.72rem',
                                        borderColor: '#CBD5E1', color: '#1A8CFF', borderRadius: '6px', py: 0.25,
                                    }}
                                >
                                    Open link
                                </Button>
                            </Box>
                        )}

                        {/* Agent (sales-person) signature — RESIDENTIAL only. The
                            signed documents are released only once both the customer
                            and the agent have signed. Hidden entirely for commercial. */}
                        {!isCommercial && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography sx={{
                                    fontFamily: PP, fontWeight: 600, fontSize: '0.75rem',
                                    color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75
                                }}>
                                    Agent Signature
                                </Typography>
                                {agentSignedLocal ? (
                                    <Box sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                                        background: '#F0FDF4', border: '1px solid #BBF7D0',
                                        borderRadius: '8px', px: 1.25, py: 0.5
                                    }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', flexShrink: 0 }} />
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#15803D', fontWeight: 500 }}>
                                            {canAgentSign ? 'You have signed' : 'Agent has signed'}
                                        </Typography>
                                    </Box>
                                ) : !canAgentSign ? (
                                    // Only the assigned sales agent may sign — an admin/creator who made
                                    // the sale on the agent's behalf sees a read-only waiting state, never
                                    // a signable pad (the stored signature must belong to the agent). (2026-08-01)
                                    <Box sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                                        background: '#F8FAFC', border: '1px dashed #CBD5E1',
                                        borderRadius: '8px', px: 1.25, py: 0.5
                                    }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#94A3B8', flexShrink: 0 }} />
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#64748B', fontWeight: 500 }}>
                                            Waiting for the sales agent to sign
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <SignaturePad onReady={(api) => { agentSigApiRef.current = api; }} />
                                        {agentSignError && (
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#EF4444', mt: 0.5 }}>
                                                {agentSignError}
                                            </Typography>
                                        )}
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={agentSigning}
                                            onClick={handleAgentSign}
                                            sx={{
                                                mt: 0.75, fontFamily: PP, textTransform: 'none', fontSize: '0.78rem',
                                                fontWeight: 600, borderRadius: '8px', boxShadow: 'none',
                                                background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                                            }}
                                        >
                                            {agentSigning
                                                ? <><CircularProgress size={13} sx={{ mr: 0.75, color: '#fff' }} />Submitting...</>
                                                : 'Submit my signature'}
                                        </Button>
                                        {!(customerSigned || signedOn) && (
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8', mt: 0.5 }}>
                                                Pending — signed documents are released once the customer also signs.
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>

                    {extraActions && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            {extraActions}
                        </>
                    )}

                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.75rem', color: '#0F172A', mb: 0.25 }}>
                            Regenerate Forms
                        </Typography>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8', mb: 0.75 }}>
                            Build the quotation &amp; forms from this sale's stored data, then download each PDF from the Documents list. Re-generate after editing the sale to refresh them.
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={generatingForms}
                            onClick={handleGenerateForms}
                            sx={{
                                fontFamily: PP, textTransform: 'none', fontSize: '0.78rem',
                                borderColor: '#CBD5E1', color: '#475569', borderRadius: '8px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {generatingForms
                                ? <><CircularProgress size={13} sx={{ mr: 0.75, color: '#94A3B8' }} />Generating...</>
                                : 'Generate forms'}
                        </Button>
                        {generateError && (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#EF4444', mt: 0.5 }}>
                                {generateError}
                            </Typography>
                        )}
                    </Box>

                    {afterActions && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            {afterActions}
                        </>
                    )}
                </>
            )}

            {/* ── Documents (collapsible): all docs list + upload ── */}
            <Divider sx={{ my: 1.5 }} />
            <Box
                onClick={() => setDocsListOpen(o => !o)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            >
                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.9rem', color: '#0F172A' }}>
                    Documents
                </Typography>
                <IconButton size="small" sx={{ color: '#64748B' }}>
                    {docsListOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Collapse in={docsListOpen} unmountOnExit>
                <Box sx={{ pt: 1 }}>
                    {/* ── Application document checklist — residential (TNB/LHDN) or
                        commercial (SSM/LHDN/TNB), by sale type ── */}
                    {(() => {
                        const checklist = isCommercial ? COMMERCIAL_CHECKLIST : RESIDENTIAL_CHECKLIST;
                        const uploadedCount = checklist.filter(
                            item => docs.some(d => d.category === 'Application' && d.title === item.key)
                        ).length;
                        const allDone = uploadedCount === checklist.length;
                        return (
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Application Checklist {isCommercial ? '(Commercial)' : '(TNB / LHDN)'}
                                    </Typography>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', fontWeight: 700, color: allDone ? '#15803D' : '#94A3B8' }}>
                                        {uploadedCount}/{checklist.length}
                                    </Typography>
                                </Box>
                                {checklist.map(item => {
                                    const itemDocs = docs.filter(d => d.category === 'Application' && d.title === item.key);
                                    const uploaded = itemDocs.length > 0;
                                    const busy = checklistUploadingKey === item.key;
                                    return (
                                        <Box key={item.key} sx={{ py: 0.35 }}>
                                            {/* Item header: status dot + label + note + Upload/Add button */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.3, px: 0.5 }}>
                                                <Box sx={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center', alignSelf: 'flex-start', mt: '4px' }}>
                                                    {uploaded
                                                        ? <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#22C55E' }} />
                                                        : <Box sx={{ width: 9, height: 9, borderRadius: '50%', border: '1.5px solid #CBD5E1' }} />}
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                                                        {item.key}{uploaded ? ` (${itemDocs.length})` : ''}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: PP, fontSize: '0.68rem', color: '#94A3B8' }}>{item.note}</Typography>
                                                </Box>
                                                {canEdit && (
                                                    <Button size="small" variant="outlined" disabled={busy}
                                                        onClick={() => handleChecklistPick(item.key)}
                                                        sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.72rem', minWidth: 0, px: 1, flexShrink: 0,
                                                              borderColor: '#CBD5E1', color: '#475569', borderRadius: '6px' }}>
                                                        {busy ? <CircularProgress size={12} sx={{ color: '#94A3B8' }} /> : (uploaded ? 'Add' : 'Upload')}
                                                    </Button>
                                                )}
                                            </Box>
                                            {/* Uploaded file(s) — same row format as the documents list below */}
                                            {itemDocs.map(doc => {
                                                const absoluteUrl = doc.fileUrl ? `${API_BASE}${doc.fileUrl}` : null;
                                                return (
                                                    <Box key={doc.salesDocumentId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5, pl: '30px', pr: 0.5, borderRadius: '6px', '&:hover': { background: '#F8FAFC' } }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            {absoluteUrl ? (
                                                                <Typography component="a" href={absoluteUrl} target="_blank" rel="noopener noreferrer"
                                                                    sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#1A8CFF', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
                                                                    {doc.fileName || doc.title}
                                                                </Typography>
                                                            ) : (
                                                                <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {doc.fileName || doc.title}
                                                                </Typography>
                                                            )}
                                                            <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8' }}>
                                                                {formatDate(doc.uploadedOn)}{doc.fileSize ? ` · ${formatBytes(doc.fileSize)}` : ''}
                                                            </Typography>
                                                        </Box>
                                                        {absoluteUrl && (
                                                            <IconButton size="small" onClick={() => handleDownloadDoc(doc)} title="Download document"
                                                                sx={{ color: '#CBD5E1', '&:hover': { color: '#1A8CFF' }, flexShrink: 0 }}>
                                                                <DownloadIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                        {canEdit && (
                                                            <IconButton size="small" onClick={() => handleDelete(doc)} title="Remove document"
                                                                sx={{ color: '#CBD5E1', '&:hover': { color: '#EF4444' }, flexShrink: 0 }}>
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    );
                                })}
                                {checklistError && (
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#EF4444', mt: 0.5 }}>{checklistError}</Typography>
                                )}
                                {canEdit && (
                                    <input ref={checklistFileRef} type="file" multiple style={{ display: 'none' }} onChange={handleChecklistFileChange} />
                                )}
                                <Divider sx={{ mt: 1.5 }} />
                            </Box>
                        );
                    })()}

                    {docsList}

                    {canEdit && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                                Upload Document
                            </Typography>

                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                <InputLabel sx={{ fontFamily: PP, fontSize: '0.82rem' }}>Category</InputLabel>
                                <Select
                                    value={uploadCategory}
                                    label="Category"
                                    onChange={e => setUploadCategory(e.target.value)}
                                    sx={{ fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' }}
                                >
                                    {CATEGORIES.map(c => (
                                        <MenuItem key={c.value} value={c.value} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                size="small"
                                label="Title / description (optional)"
                                placeholder="e.g. TNB bill, ATAP approval, signed SPA"
                                value={uploadTitle}
                                onChange={e => setUploadTitle(e.target.value)}
                                helperText="Shown as the document name. Leave blank to use the file name."
                                sx={{ mb: 1, '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontFamily: PP, fontSize: '0.82rem' }, '& .MuiFormHelperText-root': { fontFamily: PP, fontSize: '0.68rem', mx: 0 } }}
                            />

                            {isPaymentCat && (
                                <>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        required
                                        error={!!uploadError && !uploadAmount}
                                        label="Amount (RM)"
                                        value={uploadAmount}
                                        onChange={e => { setUploadAmount(e.target.value); setUploadError(''); }}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        sx={{ mb: 1, '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontFamily: PP, fontSize: '0.82rem' } }}
                                    />
                                    {(uploadCategory === 'Payment' || (uploadCategory === 'Deposit' && !depositOn)) && (
                                        <>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="date"
                                                required
                                                error={!!uploadError && !uploadDate}
                                                label={uploadCategory === 'Deposit' ? 'Deposit Date' : 'Payment Date'}
                                                value={uploadDate}
                                                onChange={e => { setUploadDate(e.target.value); setUploadError(''); }}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ mb: 1, '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontFamily: PP, fontSize: '0.82rem' } }}
                                            />
                                            {uploadCategory === 'Deposit' && (
                                                <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8', mb: 1 }}>
                                                    Sets the sale&rsquo;s deposit date (it has none yet).
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                    {uploadCategory === 'Payment' && (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8', mb: 1 }}>
                                            The date the customer paid, not today — it is what the
                                            timeline shows. Auto-labelled Payment 1, Payment 2, … in
                                            upload order.
                                        </Typography>
                                    )}
                                </>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<UploadFileIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{
                                        fontFamily: PP, textTransform: 'none', fontSize: '0.78rem',
                                        borderColor: '#CBD5E1', color: '#475569', borderRadius: '8px',
                                        whiteSpace: 'nowrap', flexShrink: 0
                                    }}
                                >
                                    Choose File
                                </Button>
                                <Typography sx={{
                                    fontFamily: PP, fontSize: '0.75rem', color: '#64748B',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1
                                }}>
                                    {selectedFile ? selectedFile.name : 'No file chosen'}
                                </Typography>
                            </Box>

                            <input
                                ref={fileInputRef}
                                type="file"
                                style={{ display: 'none' }}
                                onChange={e => {
                                    setSelectedFile(e.target.files[0] || null);
                                    setUploadError('');
                                }}
                            />

                            {uploadError && (
                                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#EF4444', mb: 0.75 }}>
                                    {uploadError}
                                </Typography>
                            )}

                            <Button
                                fullWidth
                                size="small"
                                variant="contained"
                                disabled={uploading || !selectedFile}
                                onClick={handleUpload}
                                sx={{
                                    fontFamily: PP, textTransform: 'none', fontSize: '0.8rem',
                                    fontWeight: 600, borderRadius: '8px', boxShadow: 'none',
                                    background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                                }}
                            >
                                {uploading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Upload'}
                            </Button>
                        </>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}

// Small signed/not-yet status chip for the dual-party signature line.
function SigChip({ label, done }) {
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            background: done ? '#F0FDF4' : '#F8FAFC',
            border: `1px solid ${done ? '#BBF7D0' : '#E2E8F0'}`,
            borderRadius: '8px', px: 1, py: 0.4
        }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: done ? '#22C55E' : '#CBD5E1', flexShrink: 0 }} />
            <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: done ? '#15803D' : '#64748B', fontWeight: 500 }}>
                {label}: {done ? 'signed' : 'not yet'}
            </Typography>
        </Box>
    );
}
